import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import { Warning as WarningAmberIcon } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

/**
 * Paths that remain reachable while a subscription is past-due.
 * Must stay in sync with BILLING_ALLOWED_PATH_PREFIXES in
 * backend/apexos/apps/core/permissions.py for frontend routes that mirror
 * backend allowlist endpoints.
 */
const ALLOWED_PATH_PREFIXES = [
  '/settings/billing',
  '/logout',
  '/login',
];

const isAllowedPath = (pathname: string): boolean =>
  ALLOWED_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/**
 * BillingGate blocks app access when the user's organization subscription
 * is past-due. It:
 *   1. Fetches /api/subscription/status/ (the backend returns `is_past_due`
 *      factoring in both the stored status and the current_period_end date).
 *   2. If past-due and the user isn't already on an allowed path, redirects
 *      them to /settings/billing and renders a blocking modal explaining why.
 *
 * The backend `HasActiveSubscription` permission class provides the real
 * enforcement — this component is a proactive UX layer so users see a clear
 * message instead of scattered 403 errors.
 */
export const BillingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const { data } = useSubscriptionStatus();

  const isPastDue = Boolean(data?.is_past_due) && data?.is_free !== true;
  const onAllowedPath = isAllowedPath(location.pathname);
  const message = data?.access_reason === 'card_required'
    ? `Add a payment method to start using your account.${data.trial_end ? ` Your trial ends ${new Date(data.trial_end).toLocaleDateString()}.` : ''}`
    : data?.access_reason === 'trial_expired'
      ? 'Your free trial has ended. Choose your plan in Billing to continue. Your work is saved.'
      : 'Your subscription is past due. Please add a payment method to continue using ApexOS.';

  React.useEffect(() => {
    if (isPastDue && !onAllowedPath) {
      navigate('/settings/billing', { replace: true });
    }
  }, [isPastDue, onAllowedPath, navigate]);

  // Past-due and on the billing page (or another allowed path): render the
  // page normally with a non-blocking banner so the user can actually interact
  // with "Add Payment Method".
  if (isPastDue && onAllowedPath) {
    return (
      <>
        <Alert
          color="warning"
          variant="soft"
          startDecorator={<WarningAmberIcon />}
          sx={{ borderRadius: 0 }}
        >
          {message}
          {data?.current_period_end && !data?.access_reason && (
            <Typography level="body-sm" component="span" sx={{ ml: 1 }}>
              Billing period ended{' '}
              {new Date(data.current_period_end).toLocaleDateString()}.
            </Typography>
          )}
        </Alert>
        {children}
      </>
    );
  }

  // Past-due and NOT on an allowed path: block with a modal + redirect.
  if (isPastDue && !onAllowedPath) {
    return (
      <>
        {children}
        <Modal open disableEscapeKeyDown hideBackdrop={false}>
          <ModalDialog
            variant="outlined"
            role="alertdialog"
            aria-labelledby="billing-gate-title"
            sx={{ maxWidth: 520 }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon color="var(--joy-palette-warning-500)" />
                <Typography id="billing-gate-title" level="h4">
                  {data?.access_reason ? 'Your account needs billing setup' : 'Subscription Past Due'}
                </Typography>
              </Box>
              <Alert color="warning" variant="soft">
                {message}
              </Alert>
              {data?.current_period_end && !data?.access_reason && (
                <Typography level="body-sm" color="neutral">
                  Billing period ended{' '}
                  {new Date(data.current_period_end).toLocaleDateString()}.
                </Typography>
              )}
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => logout()}
                >
                  Log out
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  onClick={() => navigate('/settings/billing', { replace: true })}
                >
                  Go to Billing
                </Button>
              </Stack>
            </Stack>
          </ModalDialog>
        </Modal>
      </>
    );
  }

  return <>{children}</>;
};

export default BillingGate;
